import PostHogClass from "../components/common/PostHogClass";

export {
    fetchRequestSend,
    detectOS,
    smoothScrollToAnchor,
    showPassword,
    setCookieHelper,
    getCookieHelper,
    // Need for CoursePassingProcessClass.js
    setCookie,
    getCookie,
    optimizeImageHelper,
    getCustomContentFromS3,
    isVisibleAndInViewport,
    isElementIntoView,
    isElementsExist,
    sendEventToAnalytics,
    getLocalDateString
};

/**
 * Performs an HTTP request to a specified URL.
 *
 * @param {string} url - The URL to which the request is sent.
 * @param {string} method - The HTTP method to be used (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The payload for the request, applicable for methods like 'POST'.
 * @param {boolean} [isFile=false] - Reserved flag kept for call-site compatibility.
 * @param {boolean} [keepalive=false] - Let the request outlive the document (see below).
 * @returns {Promise<Object>} A Promise that resolves to the parsed JSON response.
 */
const fetchRequestSend = async (url, method, paramsOrBody = null, isFile = false, keepalive = false) => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    const headers = {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
    };

    const options = {
        method: method,
        headers: headers
    };

    // Opt-in only. A normal fetch is cancelled the moment the document starts
    // unloading, which silently drops a save fired next to a navigation. `keepalive`
    // lets the browser finish the request anyway. Off by default so nothing else
    // changes behaviour; the 64KB body limit it carries is irrelevant for the
    // small JSON payloads that use it.
    if (keepalive) {
        options.keepalive = true;
    }

    const methodUpper = method.toUpperCase();

    // --- MODIFICATION START ---
    if (paramsOrBody) {
        if (methodUpper === 'GET' || methodUpper === 'HEAD') {
            // If it's GET/HEAD, treat paramsOrBody as URL parameters
            const queryParams = new URLSearchParams(paramsOrBody).toString();
            url = `${url}?${queryParams}`; // Append params to URL
        } else {
            // Otherwise, treat it as the request body
            if (paramsOrBody instanceof FormData) {
                // Don't set Content-Type for FormData
                options.body = paramsOrBody;
            } else {
                options.headers['Content-Type'] = 'application/json';
                options.body = typeof paramsOrBody !== 'string' ? JSON.stringify(paramsOrBody) : paramsOrBody;
            }
        }
    }
    // --- MODIFICATION END ---


    try {
        const response = await fetch(url, options); // URL might now include query params

        if (!response.ok) {
            // Surface the server's error message (JSON body) instead of a generic
            // status string. Still throws an Error, so existing callers keep working;
            // callers that read error.message now get the real reason.
            let body = null;
            try {
                body = await response.json();
            } catch (parseError) {
                // non-JSON error body (e.g. an HTML error page) — fall back below
            }
            const message = (body && typeof body.message === 'string' && body.message)
                ? body.message
                : `HTTP error! Status: ${response.status} ${response.statusText}`;
            const httpError = new Error(message);
            httpError.status = response.status;
            httpError.body = body;
            throw httpError;
        }
        if (response.status === 204) {
            return null;
        }
        return response.json();
    } catch (error) {
        console.error("Fetch error:", error);
        throw error; // Re-throw to be caught by caller
    }
};
/**
 * Detects the user's operating system and adds a corresponding class to the body element.
 * This function checks the user's navigator.userAgent to identify the OS and then
 * adds a class to the body element of the HTML document.
 * Supported OS classes: 'windows', 'macos', 'linux', 'ios', 'android', 'unknown-os'.
 */
function detectOS() {
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;
    let osClass = 'unknown-os';

    if (/Windows/.test(platform)) {
        osClass = 'windows';
    } else if (/Macintosh/.test(platform) || /MacIntel/.test(platform)) {
        osClass = 'macos';
    } else if (/Linux/.test(platform)) {
        osClass = 'linux';
    } else if (/iPhone/.test(userAgent) || /iPad/.test(userAgent) || /iPod/.test(userAgent)) {
        osClass = 'ios';
    } else if (/Android/.test(userAgent)) {
        osClass = 'android';
    }

    document.body.classList.add(osClass);
}

/**
 * Scrolls smoothly to the anchor element.
 * This function adds a click event listener to all anchor elements with the href attribute
 */
const smoothScrollToAnchor = () => {
    const links = document.querySelectorAll('a[href^="#"]');
    for (let link of links) {
        if (link.getAttribute('href').length > 1) {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                let target = document.querySelector(link.getAttribute('href'));
                if (target) {
                    target.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            });
        }
    }
}

const setCookie = (name, value, days) => {
    let date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    let expires = days ? "expires=" + date.toUTCString() : "";
    document.cookie = name + "=" + (value || "") + ";" + expires + ";path=/";
}

const getCookie = (cname) => {
    let name = cname + "=";
    let ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
}

const showPassword = (e) => {
    let buttons = [...document.querySelectorAll('.show-password')];
    if(!buttons.length) return;

    buttons.forEach(button => {
        button.addEventListener('click', e => {
            e.preventDefault();
            let wrapper =  button.closest('.form__field-wrapper');
            if(!wrapper) return;
            let passwordField = wrapper.querySelector('.form__field');
            const willShow = passwordField.type === 'password';
            if (willShow) {
                passwordField.type = 'text';
                wrapper.classList.add('show-password');
            } else {
                passwordField.type = 'password';
                if(wrapper.classList.contains('show-password')){
                    wrapper.classList.remove('show-password');
                }
            }
            // Keep the toggle's pressed state in sync with the field's visibility.
            button.setAttribute('aria-pressed', String(willShow));
        });
    });
}
/**
 * Sets a cookie with the specified name and value.
 * // Example Usage:
 * // setCookie('myCookie', 'myValue', { expires: 3600, path: '/' });
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to set for the cookie.
 * @param {Object} [options] - Additional options for the cookie (optional).
 * @param {number|Date} [options.expires] - The expiration date of the cookie. Can be a number (in seconds) or a Date object.
 * @param {string} [options.path] - The path for which the cookie is valid. Defaults to '/'.
 * @param {string} [options.domain] - The domain for which the cookie is valid. Defaults to the current domain.
 * @param {boolean} [options.secure] - Indicates if the cookie should only be sent over secure (HTTPS) connections.
 * @param {boolean} [options.sameSite] - Controls whether the cookie is sent with cross-origin requests. Possible values: 'Strict', 'Lax', or 'None'.
 */
const setCookieHelper = (name, value, options = {}) => {
    let expires = options.expires;

    if (typeof expires === 'number' && expires) {
        const d = new Date();
        d.setTime(d.getTime() + expires * 1000); // Convert seconds to milliseconds
        expires = options.expires = d;
    }

    if (expires && expires.toUTCString) {
        options.expires = expires.toUTCString();
    }

    let cookie = encodeURIComponent(name) + '=' + encodeURIComponent(value);

    for (const key in options) {
        if (options.hasOwnProperty(key)) {
            cookie += '; ' + key;
            const value = options[key];
            if (value !== true) {
                cookie += '=' + value;
            }
        }
    }

    document.cookie = cookie;
}

/**
 * Retrieves the value of a cookie by its name.
 * // Example Usage:
 * // const myCookieValue = getCookie('myCookie');
 * // console.log(myCookieValue);
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string|null} The value of the cookie, or null if the cookie is not found.
 */
function getCookieHelper(name) {
    const decodedName = encodeURIComponent(name) + '=';
    const cookiesArray = document.cookie.split(';');

    for (let i = 0; i < cookiesArray.length; i++) {
        let cookie = cookiesArray[i].trim();

        if (cookie.indexOf(decodedName) === 0) {
            return decodeURIComponent(cookie.substring(decodedName.length, cookie.length));
        }
    }

    return null;
}

const optimizeImageHelper = (img = null, base64Data, isBase = false) => {
    let quality = 0.9;
    let maxWidth = 1900;
    let maxHeight = 1200;
    let image = new Image();

    return new Promise((resolve, reject) => {
        if (!base64Data.startsWith('data:image')) {
            reject("Invalid base64 data for image");
            return;
        }

        image.src = base64Data;
        image.onload = function () {
            let originalWidth = image.width;
            let originalHeight = image.height;
            let originalWeight = base64Data.length;

            // Weight in bytes (900000 bytes = ~900 KB)
            if ((originalWidth > maxWidth && originalHeight > maxHeight) || originalWeight > 900000) {
                let aspectRatio = originalWidth / originalHeight;
                let newWidth, newHeight;

                if (aspectRatio > 1) {
                    newWidth = maxWidth;
                    newHeight = maxWidth / aspectRatio;
                } else {
                    newHeight = maxHeight;
                    newWidth = maxHeight * aspectRatio;
                }

                let canvas = document.createElement('canvas');
                let ctx = canvas.getContext('2d');
                canvas.width = newWidth;
                canvas.height = newHeight;

                ctx.drawImage(image, 0, 0, newWidth, newHeight);
                let optimizedBase64 = canvas.toDataURL('image/jpeg', quality);

                if (img) {
                    img.src = optimizedBase64;
                    resolve(optimizedBase64);
                } else {
                    resolve(optimizedBase64);
                }
            } else {
                if (img) {
                    img.src = base64Data;
                    resolve(base64Data);
                } else {
                    resolve(base64Data);
                }
            }
        };

        image.onerror = function () {
            reject("Failed to load base64 image");
        };
    });
};

const getCustomContentFromS3 = async (url) => {
    const data = {
        path: url,
        isCustomContent: true,
    };

    try {
        const response = await fetch(getCloudFrontContentURL, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const jsonResponse = await response.json();
        return jsonResponse.url; // This now waits for the fetch to complete.
    } catch (error) {
        console.error('Failed to fetch URL:', error);
        return false; // Return false or handle the error as needed.
    }
}


const isVisibleAndInViewport = (element) => {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const isVisible = element.offsetWidth > 0 && element.offsetHeight > 0;
    const isInViewport = (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );

    return isVisible && isInViewport;
}

const isElementIntoView = (selector, cb) => {
    const elements = [...document.querySelectorAll(selector)];

    if (elements.length) {
        const callback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    cb(entry.target);
                    observer.unobserve(entry.target);
                }
            });
        };
        const observer = new IntersectionObserver(callback, { threshold: 0 });
        elements.forEach(element => observer.observe(element));
    }
}

const isElementsExist = (selector, cb) => {
    const elements = [...document.querySelectorAll(selector)];

    if (elements.length) {
        cb(elements);
    }
}

const sendEventToAnalytics = (eventName, properties = {}, options = {}) => {
    console.log(eventName);
    // Ahrefs event sending
    if (window.AhrefsAnalytics != null) {
        window.AhrefsAnalytics.sendEvent(eventName);
    }

    // GA4 event sending
    if (typeof gtag === 'function') {
        gtag('event', eventName, properties);
    }

    // PostHog event sending (options e.g. { transport: 'sendBeacon' } for pre-redirect events)
    PostHogClass.capture(eventName, properties, options);
}

/**
 * Formats a Date as a YYYY-MM-DD calendar day in the LOCAL timezone.
 *
 * Use this instead of `toISOString().split('T')[0]` whenever the result is a
 * calendar day (a date input's value/min, a date sent to the server). toISOString
 * converts to UTC first, so it returns the next day for users east of UTC late in
 * the evening and the previous day for users west of UTC after local midnight.
 *
 * @param {Date} [date=new Date()] - The date to format; defaults to "now".
 * @returns {string} The local calendar day as YYYY-MM-DD.
 */
const getLocalDateString = (date = new Date()) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
